import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Link,
  CircularProgress
} from '@mui/material';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { trackFormError } from '../../../utils/track';
import { LoginFormType } from '../../../types/types';
import { emailValidationRegex } from '../../../constants/regex';
import { useAuth } from '../../../contexts/AuthContext';
import { useNetwork } from '../../../utils/network';
import { useUser } from '../../../contexts/UserContext';
import { useShowSnackbar } from '../../../utils/snackbar';
import PasswordField from '../../../components/PasswordField';
import { setUserId, trackEvent } from '../../../utils/googleAnalytics';
import TurnstileWidget from '../../Home/DynamicHomepage/components/CaptchaWidget/CaptchaWidget';
import { PT } from '../../../components/portalUi';
import { AuthCard } from '../../../components/AuthShell';

type LoginFormProps = {
  setRememberUser: (rememberUser: boolean) => void;
  setPreAccessToken: (preAccessToken: string | null) => void;
};

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export const LoginForm = ({
  setRememberUser,
  setPreAccessToken
}: LoginFormProps) => {
  const { markSignedIn } = useAuth();
  const { showSnackbar } = useShowSnackbar();
  const { setUser } = useUser();
  const { post } = useNetwork();

  const [loading, setLoading] = useState(false);

  // ✅ captcha/rate state
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(
    null
  );

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors }
  } = useForm<LoginFormType>();

  const onSubmit: SubmitHandler<LoginFormType> = async (data) => {
    setRememberUser(data.rememberUser ?? false);

    // if captcha is required, must have token
    if (captchaRequired && !captchaToken) {
      showSnackbar('Please complete the captcha to continue.', 'error');
      return;
    }

    setLoading(true);
    setRetryAfterSeconds(null);

    try {
      const result = await post(
        '/auth/login',
        {
          email: data.email,
          password: data.password,
          rememberUser: data.rememberUser,
          ...(captchaRequired ? { captchaToken } : {})
        },
        false
      );

      // ✅ success: clear captcha
      setCaptchaRequired(false);
      setCaptchaToken('');

      if (!!result.accessToken) {
        // The server set the httpOnly access + refresh cookies; just record the
        // session. (result.accessToken is still returned for native clients.)
        markSignedIn();
        setUser(result.user);

        setUserId(result.user.id);
        trackEvent('login', { method: 'password' });
      } else if (!!result.preAccessToken) {
        // your backend might return { requiresTwoFactor: true, preAccessToken }
        setPreAccessToken(result.preAccessToken);
      }
    } catch (e: any) {
      const status = e?.status;
      const dataResp = e?.data;

      // 429: rate limit
      if (status === 429) {
        setRetryAfterSeconds(Number(dataResp?.retryAfterSeconds || 0) || null);
        if (dataResp?.requiresCaptcha) {
          setCaptchaRequired(true);
          setCaptchaToken('');
        }
        showSnackbar(
          dataResp?.message || 'Too many attempts. Try again shortly.',
          'error'
        );
        return;
      }

      // 403: captcha required/failed
      if (status === 403 && dataResp?.requiresCaptcha) {
        setCaptchaRequired(true);
        setCaptchaToken(''); // force re-solve
        showSnackbar(dataResp?.message || 'Captcha required', 'error');
        return;
      }

      showSnackbar(dataResp?.message || 'Login failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Optional: if user edits email/password, keep captcha requirement but clear token
  const clearCaptchaToken = () => {
    if (captchaRequired) setCaptchaToken('');
    setRetryAfterSeconds(null);
  };

  return (
    <AuthCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h4">
          Login
        </Typography>
        <Typography sx={{ color: PT.textMuted, mt: 0.75, textAlign: 'center' }}>
          Welcome back — sign in to your account.
        </Typography>

        <Box sx={{ mt: 2, width: '100%' }}>
          <form
            onSubmit={handleSubmit(onSubmit, (errs) =>
              trackFormError('login', Object.keys(errs))
            )}
          >
            <Controller
              name="email"
              control={control}
              rules={{ required: true, pattern: emailValidationRegex }}
              render={({ field }) => (
                <TextField
                  id="email"
                  margin="normal"
                  required
                  fullWidth
                  label="Email"
                  autoFocus
                  autoComplete="email"
                  error={Boolean(errors.email)}
                  helperText={errors.email && 'Please enter a valid email'}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    clearCaptchaToken();
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <PasswordField
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  id="password"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    clearCaptchaToken();
                  }}
                />
              )}
            />

            <Controller
              name="rememberUser"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox color="primary" />}
                  label="Remember me"
                  {...field}
                />
              )}
            />

            {/* ✅ CAPTCHA block (only when required) */}
            {captchaRequired && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.8,
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.12)'
                }}
              >
                <Typography sx={{ fontWeight: 800, textAlign: 'center' }}>
                  Please verify you’re human
                </Typography>

                {!TURNSTILE_SITE_KEY ? (
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: 800,
                      color: 'error.main',
                      textAlign: 'center'
                    }}
                  >
                    Missing REACT_APP_TURNSTILE_SITE_KEY
                  </Typography>
                ) : (
                  <TurnstileWidget
                    siteKey={TURNSTILE_SITE_KEY}
                    onToken={(t) => setCaptchaToken(t)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                )}

                {retryAfterSeconds != null && retryAfterSeconds > 0 && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: 700,
                      opacity: 0.8,
                      textAlign: 'center'
                    }}
                  >
                    Try again in ~{retryAfterSeconds}s
                  </Typography>
                )}
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || (captchaRequired && !captchaToken)}
              startIcon={loading ? <CircularProgress size={18} /> : undefined}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>

            <Grid container>
              <Grid item xs>
                <Link
                  component={ReactRouterLink}
                  to="/set-password"
                  variant="body2"
                >
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link component={ReactRouterLink} to="/sign-up" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </AuthCard>
  );
};
